import { Observable } from 'rxjs';
import { bufferWhen, distinctUntilChanged, filter, map, shareReplay, startWith, take } from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  Inject,
  AfterViewInit,
  ComponentFactoryResolver,
  ViewChild,
} from '@angular/core';
import { SgTranslationService, UserSessionService, EnvironmentService, SgSidenavComponent } from '@sg/common';
import { ActivatedRoute, Router, ActivationEnd, NavigationEnd } from '@angular/router';
import { APP_CONFIG } from './app-config-factory';
import { SgAppConfig } from '../app-bootstrap';

/**
 * Matches the lender AI chat surface and any nested route under it,
 * but NOT sibling paths that happen to start with the same letters
 * (e.g. `/sg-lender/chat-history`). Each lookahead ensures the next
 * character is either a path/query/fragment delimiter or end-of-string.
 */
const CHAT_ROUTE_RE = /\/sg-lender\/chat(?:\/|\?|#|$)/;

@Component({
  selector: 'sg-app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppComponent implements AfterViewInit {
  logoSrc!: string;
  backgroundImgSrc!: string;
  sidenavBackgroundImgSrc!: string;
  theme!: Observable<string>;
  @ViewChild(SgSidenavComponent) sideNav: SgSidenavComponent;

  /**
   * Emits a string of all active components in the route every time the routes changes.
   * The string is space delimited and each active component will reflect through the css class: active-route-<SELECTOR>
   * where <SELECTOR> is the active selector used for the active component.
   */
  readonly activeRouteComponents: Observable<string>;

  /**
   * True when the active URL is the lender AI chat surface. The chat
   * page wants a wider mobile breakpoint, an overlay drawer, and a
   * burger button — all opt-in `@Input()`s on `SgSidenavComponent`
   * (defaults preserve the historical behavior for every other app
   * and route). Binding here keeps the chat-specific UX scoped to
   * the chat route instead of leaking into admin/borrower/analyzer.
   */
  readonly isOnChatRoute$: Observable<boolean>;

  constructor(
    public userSession: UserSessionService,
    public sgTranslation: SgTranslationService,
    public env: EnvironmentService,
    router: Router,
    cfr: ComponentFactoryResolver,
    private route: ActivatedRoute,
    @Inject(APP_CONFIG) public appConfig: SgAppConfig
  ) {
    this.theme = this.env.theme$;

    this.activeRouteComponents = router.events
      .pipe(filter((e) => e instanceof ActivationEnd && !!e.snapshot?.component))
      .pipe(
        bufferWhen(() =>
          router.events.pipe(
            filter((e) => e instanceof NavigationEnd),
            take(1)
          )
        )
      )
      .pipe(
        map((events: ActivationEnd[]) => {
          return events
            .map((e) => {
              const selector =
                typeof e.snapshot.component === 'string'
                  ? e.snapshot.component
                  : cfr.resolveComponentFactory(e.snapshot.component).selector;
              return `active-component-${selector}`;
            })
            .join(' ');
        })
      );

    // Anchored on a path-segment boundary so future siblings like
    // `/sg-lender/chat-history` or `/sg-lender/chats` don't accidentally
    // trip the chat-route bindings. `shareReplay` so the four `| async`
    // sites in the template (showMobileBurger, closeOnMobileInit,
    // mobileMode, mobileBreakpointPx) share one subscription + cached
    // value rather than each subscribing independently to router.events.
    this.isOnChatRoute$ = router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => CHAT_ROUTE_RE.test(e.urlAfterRedirects)),
      startWith(CHAT_ROUTE_RE.test(router.url)),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    // set theme based on branding
    if (this.appConfig.branding.theme) {
      this.env.changeTheme(this.appConfig.branding.theme);
    }
  }

  ngAfterViewInit(): void {
    this.logoSrc = this.appConfig.branding.logo;
    this.backgroundImgSrc = this.env.branding.backgroundImg
      ? `${this.env.getDeployUrl('/')}${this.env.branding.backgroundImg}`
      : null;
    this.sidenavBackgroundImgSrc = this.env.branding.sidenavBackgroundImg
      ? `${this.env.getDeployUrl('/')}${this.env.branding.sidenavBackgroundImg}`
      : null;

    if (this.env.branding.favIcon) {
      let link = document.createElement('link');
      link.rel = 'icon';
      link.href = `${this.env.getDeployUrl('/')}` + this.env.branding.favIcon;
      document.getElementsByTagName('head')[0].appendChild(link);
    }
  }

  changeLang(lang: string): void {
    this.sgTranslation.root.current.changeLang(lang);
  }

  logout(): void {
    this.userSession.logout();
  }

  toggleSideNav() {
    if (this.sideNav) {
      this.sideNav.toggleMenu();
    }
  }
}
